import {
    init,
    type Scope,
    type Breadcrumb,
    type BreadcrumbHint,
    type Exception,
    type EventHint,
    type BrowserOptions,
    getCurrentScope,
    replayIntegration as createReplayIntegration,
    browserTracingIntegration,
    type ErrorEvent,
    rewriteFramesIntegration,
} from '@sentry/browser';
import {init as initCapacitor} from '@sentry/capacitor';
import {UAParser} from 'ua-parser-js';

import {checkIfFlagIsToggled, DebugLocalStorageFlags} from '@famly/vet_ts-utils_local-storage-config';
import {redactor} from '@famly/vet_ts-utils_logging';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import type {RouteConfigPath} from 'web-app/react/routes/route-config';
import {findCrewFromPath, findRouteTemplateFromUncleanUrl} from 'web-app/react/routes/helpers';
import {APIErrorCode} from 'web-app/api/types';
import {RestClientNetworkError} from 'web-app/api/clients/rest-client-network-error';

import type {EnvValues} from './constants';
import {isEligibleForReplays} from './sentry-helpers';
import type {APIConfig} from './environment-helpers';

type SentryConfig = {
    debug: boolean;
    development: boolean;
    dsn: string;
    environment?: string;
    allowUrls?: string[];
    denyUrls?: string[];
    sentryOptions?: object;
};

type SentryEnvironment = {
    environment: EnvValues;
    sentry: {
        target?: 'staging' | 'production';
        staging?: SentryConfig;
        production: SentryConfig;
    };
    APP: {
        platform: string;
        version: string;
        sentryVersion: string;
        buildNumber: string;
        apiConfigs: APIConfig[];
    };
};

const FORCED_ROUTES_SAMPLE_RATE = 1;

// ChunkLoadErrors are dominated by transient causes (network blips, a deploy landing
// mid-session) that the webpack chunk-load retry in webpack-app-entry.tsx recovers from.
// We previously dropped 100% of them, leaving us blind to genuine regressions in chunk
// delivery. We now keep a small sample so dashboards retain signal without Sentry being
// flooded — multiply the sampled count by 1 / this rate to estimate the true volume.
const CHUNK_LOAD_ERROR_SAMPLE_RATE = 0.01;

const routesWithForcedTracking: RouteConfigPath[] = [
    '/account/institution/:institutionId/revenueReport',
    '/account/institution/:institutionId/billPayersReport',
    '/account/institution/:institutionId/publicFundingReport',
    '/account/institution/:institutionId/batchInvoice',
];

const getSentryConfig = (ENV: SentryEnvironment): SentryConfig | undefined => {
    const {staging, production, target} = ENV.sentry;

    switch (target) {
        case 'staging':
            return staging;
        case 'production':
            return production;
        default:
            return undefined;
    }
};

// Different browsers use different wording for network errors, so we need to check for all of them
// Produced by iterating through Sentry issues and their raw JSON payloads.
// If `value` is null, any error of that type is ignored.
// Matched by prefix, because `@sentry/browser` v10's fetch instrumentation enriches the native
// TypeError with the request hostname, e.g. `Load failed` becomes `Load failed (api.famly.de)`.
export const badNetworkErrors: {type: string; value: string | null}[] = [
    // Chrome (e.g. on Android) uses this wording for failed requests
    {type: 'TypeError', value: 'Failed to fetch'},

    // Safari uses this wording for failed requests
    {type: 'TypeError', value: 'Load failed'},
    {type: 'Error', value: 'TypeError: Load failed'},

    // Safari uses this wording for cancelled requests
    {type: 'TypeError', value: 'cancelled'},
    {type: 'Error', value: 'TypeError: cancelled'},

    // Safari uses this wording for aborted operations
    {type: 'Error', value: 'AbortError: The operation was aborted.'},

    // Represents a timeout in Chrome
    {type: 'Error', value: 'AbortError: The user aborted a request.'},

    // Represents a timeout in Safari
    {type: 'AbortError', value: 'Fetch is aborted.'},
    {type: 'AbortError', value: 'Fetch is aborted'},

    // Thrown by Apollo's RetryLink/ErrorLink when a request is cancelled mid-flight
    // (e.g. user navigates away before a query resolves). The abort is intentional — not a bug.
    {type: 'AbortError', value: 'signal is aborted without reason'},

    // Network timeouts
    {type: 'TypeError', value: 'The request timed out.'},
    {type: 'Error', value: 'Network error: The request timed out.'},
    {type: 'Error', value: 'Fetch request aborted due to timeout'},

    // No connection
    {type: 'TypeError', value: 'The Internet connection appears to be offline.'},
    {type: 'TypeError', value: 'The network connection was lost.'},

    // iOS surfaces its native URL-loading errors (NSURLError) to JavaScript as a TypeError
    // whose message is the OS' localised error description. The English wordings above only
    // catch English-locale devices, so the same connectivity failures leak from our large
    // German and Danish user bases. These are the localised equivalents seen in Sentry:
    // DNS lookup failure (NSURLErrorCannotFindHost) — the English wording isn't covered above either.
    {type: 'TypeError', value: 'A server with the specified hostname could not be found.'},
    // "The Internet connection appears to be offline." in German
    {type: 'TypeError', value: 'Es besteht anscheinend keine Verbindung zum Internet.'},
    // "cancelled" in German
    {type: 'TypeError', value: 'Abgebrochen'},
    // "cancelled" in Danish
    {type: 'TypeError', value: 'annulleret'},
];

export const isBadNetworkError = (incomingError: Exception | undefined) => {
    return badNetworkErrors.some(badNetworkError => {
        if (incomingError?.type !== badNetworkError.type) {
            return false;
        }
        if (badNetworkError.value === null) {
            return true;
        }
        return incomingError?.value?.startsWith(badNetworkError.value) ?? false;
    });
};

/**
 * Whether the given exception is a webpack chunk-load failure.
 *
 * @param incomingError the first exception attached to a Sentry event, if any
 * @returns `true` when the exception type is `ChunkLoadError`
 */
export const isChunkLoadError = (incomingError: Exception | undefined): boolean =>
    incomingError?.type === 'ChunkLoadError';

/**
 * Whether the given exception is the unactionable `InvalidStateError` raised by the
 * iOS Capacitor WebView's native media layer.
 *
 * iOS WKWebViews surface an unhandled, unsymbolicated `DOMException`
 * (`InvalidStateError: The object is in an invalid state.`, code 11) from the built-in
 * video player — e.g. while the native controls are scrubbed or the volume is changed on
 * the feed. It reaches Sentry via the global `onerror` handler as a single anonymous frame,
 * so there is nothing in our code to prevent at the throw site.
 *
 * The match is intentionally narrow (exact value + the global-`onerror` mechanism) and is
 * only applied on mobile builds by the caller, so genuine web-side `InvalidStateError`s
 * (IndexedDB, WebSocket, etc.) are still reported.
 *
 * @param incomingError the first exception attached to a Sentry event, if any
 * @returns `true` when the exception is the iOS native-media `InvalidStateError`
 */
export const isIosNativeMediaInvalidStateError = (incomingError: Exception | undefined): boolean =>
    incomingError?.type === 'Error' &&
    incomingError?.value === 'InvalidStateError: The object is in an invalid state.' &&
    incomingError?.mechanism?.type === 'auto.browser.global_handlers.onerror';

/**
 * Decides whether a sampled event should be kept for delivery to Sentry.
 *
 * @param sampleRate fraction of events to keep, in the range [0, 1]
 * @param roll a value in [0, 1), typically `Math.random()` — injected so the decision is testable
 * @returns `true` when the event falls within the sample and should be sent
 */
export const shouldKeepSampledEvent = (sampleRate: number, roll: number): boolean => roll < sampleRate;

const setupSentry = (ENV: SentryEnvironment, enableGlobalErrorCatching?: () => any) => {
    const config = getSentryConfig(ENV);

    if (!config) {
        return;
    }

    if (config.development) {
        if (config.debug) {
            // eslint-disable-next-line no-console
            console.log('`sentry` is configured for development mode.');
        }
        return;
    }

    const {dsn, environment, debug = true, allowUrls = [], denyUrls = [], sentryOptions = {}} = config;

    const isMobileBuild = ENV.APP.platform === 'ios' || ENV.APP.platform === 'android';

    // UAParser doesn't detect desktops directly, but we can infer it from the device type being undefined
    // More info here: https://github.com/faisalman/ua-parser-js/issues/182
    const uaParser = new UAParser();
    const isDesktop = uaParser.getDevice().type === undefined;

    const osFromUserAgent = uaParser.getOS();
    const osInfo =
        osFromUserAgent.name && osFromUserAgent.version
            ? {name: osFromUserAgent.name, version: osFromUserAgent.version}
            : null;

    const replayIntegration = createReplayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        unblock: [
            'svg', // We always unblock SVGs as they are only used for icons
            'img:not([src^="http"]):not([src^="data:"]):not([src^="blob:"])', // Unblock all images that aren't absolute URLs
        ],
        unmask: [
            // Elements opt in to replay unmasking by setting `data-sentry-unmask="true"`.
            // Using a dedicated attribute (instead of `data-e2e-*`) keeps this working in the
            // production bundle, where the prod webpack build strips all `data-e2e-*` attributes.
            '[data-sentry-unmask="true"]',
            '[id="topBar"]', // Unmask the top bar as it doesn't contain sensitive data
            'span.material-icons', // Unblock spans that are icons
        ],
    });

    let replaysOnErrorSampleRate = 0;
    let replaysSessionSampleRate = 0;

    // Replays are disabled on mobile builds by default
    if (isDesktop && !isMobileBuild) {
        replaysOnErrorSampleRate = 1;
        replaysSessionSampleRate = 0.0001;
    } else if (osInfo && isEligibleForReplays(osInfo)) {
        // Starting to roll out replays on the newest versions of the mobile apps
        // Both on browsers and in the native apps
        replaysOnErrorSampleRate = 1;
        replaysSessionSampleRate = 0.0001;
    } else if (checkIfFlagIsToggled('SENTRY_ENABLE_REPLAY')) {
        replaysOnErrorSampleRate = 1;
        replaysSessionSampleRate = 1;
    }

    // Requests going to these URLs will get trace headers attached automatically by the Sentry SDK. See more info at:
    // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#tracepropagationtargets
    const backendTracePropagationTargets = ENV.APP.apiConfigs
        .flatMap(config => [config.api, config.graphql])
        .map(target =>
            // The Sentry SDK checks the targets against the request URL, so we need to make sure it's a full URL that includes the origin.
            // Otherwise, things like `/api` could match a lot of other external URLs.
            // E.g. the URL `https://api.external-service.com` would match on the `/api` part.
            // If the target starts with http(s), we assume it's a full URL and don't need to add the browser's current origin.
            //
            // When making a "normal" web build, the origin is _not_ included in ENV.APP.apiConfigs
            // as we then use the browser's current origin when making requests to the backend APIs.
            // However, for mobile app builds, the origin _is_ included in ENV.APP.apiConfigs as the browser's origin in that case
            // is `capacitor://localhost`. The same is the case when we want to e.g. target the production backend from a local build.
            target.startsWith('http') ? target : `${window.location.origin}${target}`,
        );
    const tracePropagationTargets = ['localhost', ...backendTracePropagationTargets];

    try {
        // Keeping existing config values for allowUrls, for compatibility.
        const sentryConfig = {
            environment,
            allowUrls,
            denyUrls,
            debug,
            release: ENV.APP.sentryVersion,
            dist: isMobileBuild ? ENV.APP.buildNumber : undefined,
            attachStacktrace: true,
            tracePropagationTargets,
            integrations: [
                browserTracingIntegration({
                    shouldCreateSpanForRequest,
                    beforeStartSpan: options => {
                        const routeUrl = window.location.href;
                        const routeTemplate = findRouteTemplateFromUncleanUrl(routeUrl);

                        const spanName = routeTemplate || routeUrl;

                        if (options.op === 'navigation') {
                            return {
                                ...options,
                                op: 'Navigation',
                                name: spanName,
                            };
                        }

                        if (options.op === 'pageload') {
                            return {
                                ...options,
                                op: 'Pageload',
                                name: spanName,
                            };
                        }

                        return options;
                    },
                }),
                replayIntegration,
            ].filter(hasValue),
            ...sentryOptions,
            dsn,
            replaysOnErrorSampleRate,
            replaysSessionSampleRate,
            /**
             * While you're testing, enable TRACE_ALL_TRANSACTIONS flag, as that ensures that every transaction will be sent to Sentry.
             * Once testing is complete, we recommend lowering this value in production or to add the route to the forced tracking list.
             */
            tracesSampler: ({name}) => {
                if (routesWithForcedTracking.includes(name as any)) {
                    return FORCED_ROUTES_SAMPLE_RATE;
                }

                return checkIfFlagIsToggled(DebugLocalStorageFlags.TRACE_ALL_TRANSACTIONS.flag) ? 1 : 0.01;
            },
            beforeSend: (event: ErrorEvent, hint: EventHint) => {
                // Skipping event filtering when the in diagnostics mode
                if (checkIfFlagIsToggled(DebugLocalStorageFlags.SENTRY_DIAGNOSTIC_MODE.flag)) {
                    // Adding a flag to easily identify events that are sent in diagnostic mode
                    if (event.tags) {
                        event.tags.diagnosticMode = true;
                    } else {
                        event.tags = {
                            diagnosticMode: true,
                        };
                    }

                    return event;
                }

                // Attempt to map the route to a crew (to assist with auto assigning issues to the relevant crew)
                if (event.request?.url) {
                    const routeTemplate = findRouteTemplateFromUncleanUrl(event.request.url);

                    if (routeTemplate) {
                        const crew = findCrewFromPath(routeTemplate);

                        if (crew) {
                            event.tags = {
                                ...event.tags,
                                route: routeTemplate,
                                crew,
                            };
                        }
                    }
                }

                const firstException = event?.exception?.values?.[0];
                const stackTrace = firstException?.stacktrace?.frames ?? [];

                // Issues caused by extensions in Safari
                // In newer versions of the Sentry service I believe these are excluded automatically.
                // https://github.com/getsentry/sentry-javascript/discussions/5875
                if (stackTrace.some(frame => frame.filename?.startsWith('webkit-masked-url://hidden/'))) {
                    return null;
                }

                // Used for e.g. filtering Sentry events
                // https://docs.sentry.io/platforms/javascript/guides/electron/configuration/options/#before-send
                if (firstException?.value === 'TransitionAborted') {
                    return null;
                }

                // If the error is a network error we want to add the response to the event
                if (hint.originalException instanceof RestClientNetworkError) {
                    event.extra = {...(event.extra ?? {}), response: hint.originalException.response};

                    // Filter out errors from the /client-events endpoint (analytics/famlytics)
                    // These are non-critical and the service can sometimes be unavailable
                    // Only ignore when the status code is >= 500
                    if (
                        hint.originalException.message?.includes('/client-events') &&
                        hint?.originalException?.response?.status >= 500
                    ) {
                        return null;
                    }
                }

                if (event?.extra) {
                    // We see that some events are sent to Sentry with a __serialized__ extra property.
                    // This happens when a non-Error object is passed to Sentry.captureException().
                    // Documented here: https://docs.sentry.io/platforms/javascript/troubleshooting/#events-with-non-error-exception
                    const serializedExtras = event.extra.__serialized__ as any;

                    // We don't want to send events to Sentry related to expiring sessions
                    if (serializedExtras?.responseData?.errorCode === APIErrorCode.SESSION_EXPIRED) {
                        return null;
                    }
                }

                // We're not interested in logging network connectivity errors to Sentry
                if (isBadNetworkError(firstException)) {
                    return null;
                }

                // ChunkLoadErrors are high-volume and mostly transient, so we keep only a sample.
                // Tag the survivors with the rate they were sampled at so dashboards can extrapolate
                // the true volume (count / rate) and tell sampled noise apart from a real regression.
                if (isChunkLoadError(firstException)) {
                    if (!shouldKeepSampledEvent(CHUNK_LOAD_ERROR_SAMPLE_RATE, Math.random())) {
                        return null;
                    }
                    event.tags = {...event.tags, sampled: 'chunk-load-error', sampleRate: CHUNK_LOAD_ERROR_SAMPLE_RATE};
                }

                // Stripe's SDK throws IntegrationErrors when mobile webviews suspend or discard
                // the iframe hosting the PaymentElement (e.g. app backgrounded, memory pressure).
                // These are internal Stripe errors we cannot prevent in our code.
                const isStripeError =
                    firstException?.type === 'IntegrationError' &&
                    stackTrace.some(frame => frame.filename?.includes('js.stripe.com')) &&
                    (firstException?.value?.includes('We could not retrieve data from the specified Element') ||
                        firstException?.value?.includes('This Element has already been destroyed'));

                if (isStripeError) {
                    return null;
                }

                // iOS Capacitor WebViews raise an unhandled, unsymbolicated InvalidStateError from
                // the native video player (see isIosNativeMediaInvalidStateError). It's unactionable
                // platform noise, so drop it on mobile builds only — web-side InvalidStateErrors
                // (IndexedDB, WebSocket, etc.) are still worth reporting.
                if (isMobileBuild && isIosNativeMediaInvalidStateError(firstException)) {
                    return null;
                }

                return event;
            },
            // It's import that we ignore errors _before_ submitting events to our Sentry servers.
            // Not only does this lower load, it also avoids capturing Replays that we're not interested in.
            ignoreErrors: [
                'The OS most likely terminated your app because it overused RAM',
                'Received a memory warning from applicationDidReceiveMemoryWarning',
                // We're seeing a lot of these in Sentry after introducing ResizeObserver in Famly Home (https://github.com/famly/app/pull/20149).
                // They're flooding our Sentry infrastructure, and they're seemingly not causing any issues for end users,
                // so we're ignoring them for now.
                'ResizeObserver loop completed with undelivered notifications',
                // Error raised by Quill that's not user-facing.
                'a.domNode.getBoundingClientRect is not a function',
                // Random network errors:
                'Network error when calling',
                'undefined - /*',
                // Sometimes `famlygathering` can't keep up with all events. We're okay with not logging this.
                'Service Unavailable - /client-events',
                '- /client-events',
                // These are raised somewhere outside of our code in Capacitor builds for iOS, and do not seem to affect users.
                "Can't find variable: EmptyRanges",
                // The iOS Share plugin rejects with "Share canceled" when the user dismisses the native
                // share sheet without picking a target. That's an expected user action, not an error.
                'Share canceled',
                // These seem to be caused by extensions (Google Translate):
                "undefined is not an object (evaluating 'a.J')",
                "undefined is not an object (evaluating 'a.H')",
                // These seem to be part of the native video support in iOS:
                "undefined is not an object (evaluating 'media.currentTime')",
                "undefined is not an object (evaluating 'media.duration')",
                "undefined is not an object (evaluating 'media.webkitPresentationMode')",
                "undefined is not an object (evaluating 'media.webkitSupportsPresentationMode')",
                "undefined is not an object (evaluating 'this.media.readyState')",
                "undefined is not an object (evaluating 'this.media.videoTracks')",
                "undefined is not an object (evaluating 'this.mediaController.media.audioTracks')",
                "undefined is not an object (evaluating 'this.mediaController.media.webkitCurrentPlaybackTargetIsWireless')",
                // Third-party extensions are raising this error in Safari (and Mobile Safari) 18.x and 26.x (last spotted version was 26.1 in October 2025)
                'Invalid call to runtime.sendMessage()',
                /failed loading locales.*\.json: Load failed/i,
                /failed parsing locales.*\.json to json/i,
            ],
        } satisfies BrowserOptions;

        const beforeBreadcrumb = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => {
            if (breadcrumb.category === 'console' && breadcrumb.level === 'warning') {
                // Filter out console warnings as they are typically not useful
                return null;
            }

            // Change the breadcrumb if it's a GraphQL request so we can see which operation was called
            breadcrumb = transformGraphQLBreadcrumb(breadcrumb, hint);

            return breadcrumb;
        };

        if (isMobileBuild) {
            initCapacitor(
                {
                    dsn: sentryConfig.dsn,
                    release: sentryConfig.release,
                    debug: sentryConfig.debug,
                    allowUrls: sentryConfig.allowUrls,
                    // We don't set denyUrls here, since there's no URLs we want to deny on mobile builds
                    beforeSend: sentryConfig.beforeSend,
                    ignoreErrors: sentryConfig.ignoreErrors,
                    attachStacktrace: sentryConfig.attachStacktrace,
                    environment: sentryConfig.environment,
                    integrations: [
                        // This integration rewrites stack frames, which we need specifically for Android,
                        // where stack frames start with `/`, which Sentry doesn't like.
                        // The default integration prefixes stack frames with `app://`, which works for us.
                        rewriteFramesIntegration(),
                        replayIntegration,
                    ].filter(hasValue),
                    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
                    replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
                    beforeBreadcrumb,
                    // Mobile sentry specyfic options
                    enableAppHangTracking: true,
                    // Sentry's iOS SDK expects this threshold in seconds, not milliseconds
                    appHangTimeoutInterval: 10,
                },
                init,
            );
        } else {
            init({
                ...sentryConfig,
                beforeBreadcrumb,
            });
        }
    } catch (e) {
        console.warn(`Error during 'sentry' initialization: ${e}`); // tslint:disable-line no-console
        return;
    }

    if (enableGlobalErrorCatching) {
        enableGlobalErrorCatching();
    }

    configureSentryScope(scope => {
        scope.addEventProcessor((event, hint) => {
            // This attempts to filter out errors that the backend hints that we shouldn't log.
            if ((hint?.originalException as any)?.response?.log === false) {
                return null;
            }

            /**
             * This attempts to filter out errors that arises from not
             * manually catching errors when calling GraphQL mutations.
             * But only in the case where the backend, via the 'log' property,
             * has hinted not to log that error.
             */
            if ((hint?.originalException as any)?.graphQLErrors?.every?.(error => error?.extensions?.log === false)) {
                return null;
            }

            return event;
        });
    });
};

// Function to update the breadcrumb if it's determined as a GraphQL request
const transformGraphQLBreadcrumb = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb => {
    // Type checks
    if (!isGraphQLRequest(breadcrumb) || !hasValue(hint) || !hasValue(breadcrumb.data)) {
        return breadcrumb;
    }

    // Attempt to parse operation name
    const {operationName, type, variables} = getGraphQLDataFromHint(hint);

    if (!hasValue(operationName)) {
        return breadcrumb;
    }

    // Finally transform breadcrumb
    return {
        ...breadcrumb,
        type: 'query',
        category: 'GraphQL',
        message: operationName,
        data: {
            ...breadcrumb.data,
            type,
            variables: variables && redactor(variables),
        },
    };
};

// Function to determine if breadcrumb is a GraphQL request
const isGraphQLRequest = (breadcrumb: Breadcrumb) => {
    return (breadcrumb.category === 'fetch' && breadcrumb.data?.url?.includes('graphql')) ?? false;
};

// Attempts to get the operation name from the breadcrumb hint
const getGraphQLDataFromHint = (hint: BreadcrumbHint) => {
    // Type check
    if (!hasValue(hint.input)) {
        return {};
    }
    const [, requestData] = hint.input;

    // More type checking
    if (!hasValue(requestData) || !hasValue(requestData.body) || typeof requestData.body !== 'string') {
        return {};
    }

    // Attempt to parse body and read operation name from query/mutation
    try {
        const requestBody = JSON.parse(requestData.body) as {operationName: string; query: string; variables?: object};

        if (!hasValue(requestBody.operationName)) {
            return {};
        }

        return {
            operationName: requestBody.operationName,
            variables: requestBody.variables,
            type: requestBody.query.startsWith('query') ? ('query' as const) : ('mutation' as const),
        };
    } catch {
        // Return {} if JSON.parse fails
        return {};
    }
};

export const configureSentryScope = (callback: (scope: Scope) => void) => {
    callback(getCurrentScope());
};

/**
 * Filtering function used to determine whether a span should be
 * created for a specific HTTP request. See more in Sentry's documentation:
 *
 * https://docs.sentry.io/platforms/javascript/guides/nextjs/tracing/instrumentation/automatic-instrumentation/#shouldcreatespanforrequest
 *
 * @param url The URL of the HTTP request
 * @returns `true` if a span should be created, `false` otherwise
 */
export const shouldCreateSpanForRequest = (url: string) => {
    const isUrlBlockListed = denyListedEndpoints.some(blockListedUrl => url.includes(blockListedUrl));

    return !isUrlBlockListed;
};

// We don't want to include health checks, notification updates or Famlytics events in our traces
// as they are called on an interval and will just cause noise.
const denyListedEndpoints = ['graphql?KeepAlive', 'graphql?NotificationUpdates', 'client-events'];

export default setupSentry;
